/**
 * Catalog contact + newsletter forms (all surfaces).
 *
 * Native Shopify POST only (CAPTCHA-safe). Each surface owns its success UI via
 * return_to query params:
 *   newsletter → ?customer_posted=true&identity_source={footer|popup|cart|home|pdp}
 *   contact    → ?contact_posted=true
 *
 * CAPTCHA: bind on focus by default; protect() only on submit (and when the
 * hidden popup opens).
 *
 * Success reliability: Shopify often ignores return_to query strings after
 * CAPTCHA. We stash the absolute success URL + source, rescue /contact dumps
 * and same-path returns that lost the query (referrer /contact), and can
 * paint success client-side from the form's <template> as a last resort.
 */
(() => {
  if (window.__catalogContactFormInit) return;
  window.__catalogContactFormInit = true;

  const NEWSLETTER_ATTR = 'data-catalog-newsletter-form';
  const CONTACT_ATTR = 'data-catalog-contact-form';
  const RETURN_STORAGE_KEY = 'catalog_form_return_to';
  const RETURN_COOKIE = 'catalog_form_return_to';
  const LEGACY_STORAGE_KEY = 'catalog_newsletter_return_to';
  const SUCCESS_SOURCE_KEY = 'catalog_form_success_source';
  const SUCCESS_PATH_KEY = 'catalog_form_success_path';

  function referrerIsContact() {
    try {
      if (!document.referrer) return false;
      const ref = new URL(document.referrer);
      if (ref.origin !== window.location.origin) return false;
      return ref.pathname === '/contact' || ref.pathname === '/contact/';
    } catch (_error) {
      return false;
    }
  }

  const FormReturn = {
    stash(url, source, path) {
      if (!url) return;
      try {
        sessionStorage.setItem(RETURN_STORAGE_KEY, url);
        sessionStorage.removeItem(LEGACY_STORAGE_KEY);
        if (source) sessionStorage.setItem(SUCCESS_SOURCE_KEY, source);
        if (path) sessionStorage.setItem(SUCCESS_PATH_KEY, path);
      } catch (_error) {
        /* private mode */
      }
      try {
        document.cookie = `${RETURN_COOKIE}=${encodeURIComponent(url)};path=/;max-age=120;SameSite=Lax`;
      } catch (_error) {
        /* ignore */
      }
    },
    read() {
      try {
        const fromSession =
          sessionStorage.getItem(RETURN_STORAGE_KEY) || sessionStorage.getItem(LEGACY_STORAGE_KEY);
        if (fromSession) return fromSession;
      } catch (_error) {
        /* ignore */
      }
      try {
        const match = document.cookie.match(new RegExp(`(?:^|; )${RETURN_COOKIE}=([^;]*)`));
        if (match) return decodeURIComponent(match[1]);
      } catch (_error) {
        /* ignore */
      }
      return '';
    },
    readSource() {
      try {
        return sessionStorage.getItem(SUCCESS_SOURCE_KEY) || '';
      } catch (_error) {
        return '';
      }
    },
    readPath() {
      try {
        return sessionStorage.getItem(SUCCESS_PATH_KEY) || '';
      } catch (_error) {
        return '';
      }
    },
    clear() {
      try {
        sessionStorage.removeItem(RETURN_STORAGE_KEY);
        sessionStorage.removeItem(LEGACY_STORAGE_KEY);
        sessionStorage.removeItem(SUCCESS_SOURCE_KEY);
        sessionStorage.removeItem(SUCCESS_PATH_KEY);
      } catch (_error) {
        /* ignore */
      }
      try {
        document.cookie = `${RETURN_COOKIE}=;path=/;max-age=0;SameSite=Lax`;
      } catch (_error) {
        /* ignore */
      }
    },
  };

  window.__catalogFormReturn = FormReturn;

  function getNewsletterSubmitButton(form) {
    return form.querySelector('.newsletter-form__button[type="submit"], button[type="submit"]');
  }

  function setSubmitLoading(submitButton, isLoading) {
    if (!(submitButton instanceof HTMLButtonElement)) return;

    submitButton.classList.toggle('loading', isLoading);
    submitButton.setAttribute('aria-busy', isLoading ? 'true' : 'false');

    const spinner = submitButton.querySelector('.loading__spinner');
    if (spinner instanceof HTMLElement) {
      spinner.classList.toggle('hidden', !isLoading);
      spinner.style.removeProperty('display');
      spinner.hidden = !isLoading;
    }
  }

  function resetFormSubmitState(form) {
    if (!(form instanceof HTMLFormElement)) return;
    delete form.dataset.catalogCaptchaReady;
    delete form.dataset.catalogNewsletterSubmitting;
    delete form.dataset.catalogContactSubmitting;
    const submitButton = form.hasAttribute(CONTACT_ATTR)
      ? form.querySelector('[data-catalog-contact-submit], button[type="submit"]')
      : getNewsletterSubmitButton(form);
    setSubmitLoading(submitButton, false);
  }

  function resetAllFormSubmitState() {
    document
      .querySelectorAll(`[${CONTACT_ATTR}], [${NEWSLETTER_ATTR}]`)
      .forEach((form) => resetFormSubmitState(form));
  }

  function prepareShopifyContactEndpoint(form) {
    try {
      const url = new URL(form.getAttribute('action') || '/contact', window.location.origin);
      url.hash = '';
      if (!url.pathname || url.pathname === '/') {
        url.pathname = '/contact';
      }
      form.setAttribute('action', `${url.pathname}${url.search}`);
    } catch (_error) {
      form.setAttribute('action', '/contact');
    }
  }

  function currentReturnPath() {
    let path = window.location.pathname || '/';
    if (!path || path === '/contact' || path === '/contact/') {
      path = '/';
    }
    return path;
  }

  function ensureReturnTo(form, kind) {
    let path = currentReturnPath();
    let relative;
    let absolute;
    let source = '';

    if (kind === 'contact') {
      if (path === '/' || path === '/contact' || path === '/contact/') {
        path = '/pages/contact-us';
      }
      relative = `${path}?contact_posted=true`;
      absolute = `${window.location.origin}${relative}`;
      source = 'contact';
    } else {
      source = form.dataset.identitySource || 'footer';
      relative = `${path}?customer_posted=true&identity_source=${encodeURIComponent(source)}`;
      absolute = `${window.location.origin}${relative}`;
    }

    let input = form.querySelector('input[name="return_to"]');
    if (!(input instanceof HTMLInputElement)) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'return_to';
      form.appendChild(input);
    }
    input.value = relative;
    FormReturn.stash(absolute, source, path);
    return absolute;
  }

  function openCartIfNeeded(source) {
    if (source !== 'cart') return;
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open(document.querySelector('cart-icon-bubble') || document.body);
    }
  }

  function applyNewsletterSuccessClient(source) {
    const form = document.querySelector(
      `[data-catalog-newsletter-form][data-identity-source="${source}"]`
    );
    if (!(form instanceof HTMLFormElement)) return false;
    if (form.querySelector('[data-catalog-newsletter-success]')) {
      form.classList.add('newsletter-form--success');
      return true;
    }

    const template = form.querySelector('template[data-catalog-newsletter-success-template]');
    if (!(template instanceof HTMLTemplateElement)) return false;

    form.querySelectorAll('.newsletter-form__field-wrapper').forEach((el) => el.remove());
    form.appendChild(template.content.cloneNode(true));
    form.classList.add('newsletter-form--success');

    if (source === 'popup') {
      const root = document.querySelector('[data-catalog-identity-popup]');
      if (root instanceof HTMLElement) {
        root.classList.add('catalog-identity-popup--success');
        root.hidden = false;
        root.setAttribute('data-catalog-identity-popup-success', '1');
        document.documentElement.classList.add('catalog-identity-popup-open');
        const success = root.querySelector('[data-catalog-newsletter-success]');
        if (success instanceof HTMLElement) success.focus({ preventScroll: true });
        window.setTimeout(() => {
          if (root.hidden) return;
          root.hidden = true;
          document.documentElement.classList.remove('catalog-identity-popup-open');
        }, window.matchMedia('(max-width: 749px)').matches ? 12000 : 8000);
      }
    } else {
      const success = form.querySelector('[data-catalog-newsletter-success]');
      if (success instanceof HTMLElement) {
        success.focus({ preventScroll: true });
        success.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }

    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'email_captured', identity_source: source });
      if (source === 'popup') {
        window.dataLayer.push({ event: 'identity_popup_success_shown' });
      }
      localStorage.setItem('catalog_identity_email_captured', '1');
      localStorage.setItem('catalog_identity_popup_dismissed', '1');
    } catch (_error) {
      /* ignore */
    }

    return true;
  }

  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Enter' || event.shiftKey || event.isComposing) return;

      const target = event.target;
      if (!(target instanceof HTMLInputElement) || target.type !== 'email') return;

      const form = target.form;
      if (!(form instanceof HTMLFormElement) || !form.hasAttribute(NEWSLETTER_ATTR)) return;
      if (form.classList.contains('newsletter-form--success')) return;
      if (form.dataset.catalogNewsletterSubmitting === '1') return;

      const submitButton = getNewsletterSubmitButton(form);
      if (!(submitButton instanceof HTMLButtonElement)) return;

      event.preventDefault();
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit(submitButton);
      } else if (form.reportValidity()) {
        submitButton.click();
      }
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      const isContact = form.hasAttribute(CONTACT_ATTR);
      const isNewsletter = form.hasAttribute(NEWSLETTER_ATTR);
      if (!isContact && !isNewsletter) return;
      if (!form.checkValidity()) return;

      const submitButton = isContact
        ? form.querySelector('[data-catalog-contact-submit], button[type="submit"]')
        : getNewsletterSubmitButton(form);
      if (!(submitButton instanceof HTMLButtonElement)) return;

      const kind = isContact ? 'contact' : 'newsletter';

      if (form.dataset.catalogCaptchaReady === '1') {
        prepareShopifyContactEndpoint(form);
        ensureReturnTo(form, kind);
        if (isNewsletter) form.dataset.catalogNewsletterSubmitting = '1';
        if (isContact) form.dataset.catalogContactSubmitting = '1';
        setSubmitLoading(submitButton, true);
        return;
      }

      event.preventDefault();
      prepareShopifyContactEndpoint(form);
      ensureReturnTo(form, kind);
      setSubmitLoading(submitButton, true);

      if (isNewsletter && form.dataset.catalogNewsletterSubmitting === '1') return;
      if (isContact && form.dataset.catalogContactSubmitting === '1') return;
      if (isNewsletter) form.dataset.catalogNewsletterSubmitting = '1';
      if (isContact) form.dataset.catalogContactSubmitting = '1';

      const finishNativeSubmit = () => {
        form.dataset.catalogCaptchaReady = '1';
        ensureReturnTo(form, kind);
        HTMLFormElement.prototype.submit.call(form);
      };

      const captchaProtect = window.Shopify && window.Shopify.captcha && window.Shopify.captcha.protect;
      if (typeof captchaProtect === 'function') {
        try {
          captchaProtect(form, finishNativeSubmit);
        } catch (_error) {
          resetFormSubmitState(form);
        }
      } else {
        finishNativeSubmit();
      }
    },
    true
  );

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) resetAllFormSubmitState();
  });

  document.addEventListener('DOMContentLoaded', () => {
    const params = new URLSearchParams(window.location.search);
    const newsletterPosted = params.get('customer_posted') === 'true';
    const contactPosted = params.get('contact_posted') === 'true';
    const path = window.location.pathname || '/';
    const onContactDump = path === '/contact' || path === '/contact/';
    const pending = FormReturn.read();
    const fromContact = referrerIsContact();

    // Same-path return that lost ?customer_posted= — finish the success URL.
    if (!newsletterPosted && !contactPosted && !onContactDump && pending && fromContact) {
      try {
        const pendingUrl = new URL(pending, window.location.origin);
        const pendingPath = pendingUrl.pathname || '/';
        if (pendingPath === path || pendingPath.replace(/\/$/, '') === path.replace(/\/$/, '')) {
          FormReturn.clear();
          window.location.replace(pending);
          return;
        }
      } catch (_error) {
        /* fall through */
      }
    }

    // Client paint if we still lack query params but just came back from /contact.
    if (!newsletterPosted && !contactPosted && fromContact) {
      const source = FormReturn.readSource();
      const successPath = FormReturn.readPath();
      if (source && source !== 'contact' && successPath === path) {
        applyNewsletterSuccessClient(source);
        openCartIfNeeded(source);
        FormReturn.clear();
        return;
      }
    }

    if (!onContactDump && !newsletterPosted && !contactPosted && pending && !fromContact) {
      FormReturn.clear();
    }

    if (!newsletterPosted && !contactPosted) return;

    FormReturn.clear();

    if (newsletterPosted) {
      const source = params.get('identity_source') || '';
      openCartIfNeeded(source);
      if (source !== 'popup') {
        const success = document.querySelector(
          source
            ? `[data-catalog-newsletter-form][data-identity-source="${source}"] [data-catalog-newsletter-success]`
            : '[data-catalog-newsletter-success]'
        );
        if (success instanceof HTMLElement) {
          success.closest('form')?.classList.add('newsletter-form--success');
          success.focus({ preventScroll: true });
          success.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }
      params.delete('customer_posted');
      params.delete('identity_source');
    }

    if (contactPosted) {
      const success = document.querySelector('[data-catalog-contact-success]');
      if (success instanceof HTMLElement) {
        success.focus({ preventScroll: true });
        success.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
      params.delete('contact_posted');
    }

    const query = params.toString();
    const nextUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
    window.history.replaceState({}, '', nextUrl);
  });
})();
